import { IComponent } from "../../components/component.interface";
import { PnlComponent } from "../../components/pnl.component";
import { DateHelpers, getHolidayManagerInstance } from "../../helpers/date-helpers";
import { TraceLoggingHelpers } from "../../helpers/trace-logging-helpers";
import { IChartContainer } from "../base/chart-container.interface";
import { TIChartPointModel } from "../data-point/ti-chart-point.model";
import { Timeframe } from "../enum/timeframe.enum";
import { SingleStockWindowDataModel } from "./single-stock-window-data.model";

interface LastMarketCloseChangeFromCloseResult {
    symbol: string;
    requestTime: Date;
    lastMarketCloseTime: Date;
    lastMarketCloseDataPoint: TIChartPointModel;
    lastMarketClosePrice: number;
    lastPrice: number;
    changesFromCloseDollar: number;
    changesFromClosePercentage: number;
}

export interface ChangeFromCloseValueResult {
    changesFromCloseDollar: number;
    changesFromClosePercentage: number;
}
export class SingleStockWindowChangeFromCloseManager implements IComponent {
    public id = '';
    
    private initTiDataPoints = false;
    private initSingleStock = false;
    private initLastPriceInLastMarketPoints = false;

    private base: IChartContainer;
    private points: Array<TIChartPointModel> = [];
    private lastPriceInLastMarketPoints: Array<TIChartPointModel> = [];
    private singleStockData: SingleStockWindowDataModel | null = null;
    private changesFromCloseDollar = 0;
    private changeFromClosePercentage = 0;
    private symbol = '';

    private readonly timeframe = Timeframe.Day;
    private readonly lastPriceInLastMarketPointsTimeframe = Timeframe.FifteenMinute;

    constructor(base: IChartContainer) {
        this.base = base;
        this.subscribeChartDataService();
    }

    private updateSymbol(symbol: string) {
        this.symbol = symbol;
        this.initLastPriceInLastMarketPoints = false;
        this.initSingleStock = false;
        this.initTiDataPoints = false;
        this.points = [];
        this.lastPriceInLastMarketPoints = [];
        this.singleStockData = null;
        this.resolveGetChangeFromCloseValues = null;
        this.resolveGetLastPriceInLastMarketOpenDay = null;
    }

    public updateSingleStockData(singleStockData: SingleStockWindowDataModel) {
        this.singleStockData = singleStockData;
        this.initSingleStock = true;
    }

    public get singleStockValueData() {
        return this.singleStockData;
    }

    private resolveGetChangeFromCloseValues: any;
    public getChangeFromCloseValues(): Promise<ChangeFromCloseValueResult | null> {
        return new Promise<ChangeFromCloseValueResult | null>((r) => {
            if (!this.base.tiChartSeries.hasAnyDataPoints()) {
                r(null);
                return;
            }

            const dateNow = new Date();
            const shouldUseTopListForChangeFromClose = this.getShouldUseTopListForChangeFromClose(dateNow);
            if (shouldUseTopListForChangeFromClose) {
                // Use TopList for ChangeFromClose and ChangeFromClosePercent
                // call setChangeFromClose_FromTopList(...)
                if (!this.initSingleStock || !this.singleStockData || SingleStockWindowDataModel.doesNotHaveValue(this.singleStockData.changesFromCloseDollar) || SingleStockWindowDataModel.doesNotHaveValue(this.singleStockData.changesFromClosePercentage)) {
                    r(null);
                    return;
                }
                
                if (this.base.marketingWebChartMode) {
                    this.setChangeFromClose_FromTopList(this.singleStockData?.changesFromCloseDollar ?? 0, this.singleStockData?.changesFromClosePercentage ?? 0);
                } else {
                    this.setChangeFromClose_FromTopList(this.singleStockData?.changesFromCloseDollar ?? 0, this.singleStockData?.changesFromClosePercentage ?? 0);
                }
            }
            else {
                if (this.base.marketingWebChartMode && SingleStockWindowDataModel.hasValue(this.singleStockData.changesFromCloseDollar) && SingleStockWindowDataModel.hasValue(this.singleStockData.changesFromClosePercentage)) {
                    this.setChangeFromClose_FromTopList(this.singleStockData?.changesFromCloseDollar ?? 0, this.singleStockData?.changesFromClosePercentage ?? 0);
                    r({changesFromCloseDollar: this.changesFromCloseDollar, changesFromClosePercentage: this.changeFromClosePercentage});
                    return;
                } 

                // call getLastMarketClose_ChangeFromClose
                // call setChangeFromClose_FromLastMarketOpen
                if (!this.initTiDataPoints) {
                    this.resolveGetChangeFromCloseValues = r;
                    this.base.chartService.requestSimpleTiDataPoints(this.symbol, this.timeframe, false, false, 350);
                    return;
                }
                
                const result = this.getLastMarketClose_ChangeFromClose(dateNow);
                this.setChangeFromClose_FromLastMarketOpen(result?.changesFromCloseDollar ?? 0, result?.changesFromClosePercentage ?? 0);
            }
            
            r({changesFromCloseDollar: this.changesFromCloseDollar, changesFromClosePercentage: this.changeFromClosePercentage});
        })
    }

    private processAndReturnGetChangeFromClose() {
        if (!this.resolveGetChangeFromCloseValues) return;

        const dateNow = new Date();
        const result = this.getLastMarketClose_ChangeFromClose(dateNow);
        this.setChangeFromClose_FromLastMarketOpen(result?.changesFromCloseDollar ?? 0, result?.changesFromClosePercentage ?? 0);

        this.resolveGetChangeFromCloseValues({changesFromCloseDollar: this.changesFromCloseDollar, changesFromClosePercentage: this.changeFromClosePercentage});
        this.resolveGetChangeFromCloseValues = null;
    }

    public getShouldUseTopListForChangeFromClose(currentTime: Date): boolean {
        const isMarketOpen = this.isMarketOpen(currentTime);
        const isPreMarket = this.isPremarket(currentTime);
        const isPostMarket = this.isPostmarket(currentTime);

        //const shouldUseTopListData = (isPreMarket || isMarketOpen || isPostMarket);
        const shouldUseTopListData = isPreMarket || isMarketOpen || isPostMarket;

        //TraceLoggingHelpers.log(`getShouldUseTopListForChangeFromClose, shouldUseTopListData: ${shouldUseTopListData}, currentTime: ${currentTime}, isPreMarket: ${isPreMarket}, isMarketOpen: ${isMarketOpen}, isPostMarket: ${isPostMarket}`);
        TraceLoggingHelpers.log(`getShouldUseTopListForChangeFromClose, shouldUseTopListData: ${shouldUseTopListData}, currentTime: ${currentTime}, isPreMarket: ${isPreMarket}, isMarketOpen: ${isMarketOpen}, isPostMarket: ${isPostMarket}`);

        return shouldUseTopListData;
    }

    private subscribeChartDataService() {
        this.base.chartService.subscribe_SimpleTiDataPoints((dataPoints: Array<TIChartPointModel>, symbol: string, timeframe: Timeframe) => {
            if (this.symbol == symbol) {
                if (this.timeframe == timeframe) {
                    this.points = dataPoints;
                    this.initTiDataPoints = true;
                    this.processAndReturnGetChangeFromClose();
                }
                if (this.lastPriceInLastMarketPointsTimeframe == timeframe) {
                    this.lastPriceInLastMarketPoints = dataPoints;
                    this.initLastPriceInLastMarketPoints = true;
                    this.processAndReturnGetLastPriceInLastMarketOpenDay();
                }
            }
        });
        this.base.chartService.subscribeChartRequestingNewSymbolData((symbol: string, timeframe: Timeframe) => {
            if (this.symbol === symbol) return;

            this.updateSymbol(symbol);
        });
    }

    private isMarketOpen(currentTime: Date): boolean {
        const currentTimeMarketTimeZone = DateHelpers.toMarketTimeZone(currentTime);
        const marketOpen = DateHelpers.getMarketOpenTime(currentTimeMarketTimeZone.toJSDate());
        const marketClose = DateHelpers.getMarketCloseTime(currentTimeMarketTimeZone.toJSDate());
        return DateHelpers.isNormalWorkingDay(currentTimeMarketTimeZone) && currentTimeMarketTimeZone.valueOf() >= marketOpen.valueOf() && currentTimeMarketTimeZone.valueOf() <= marketClose.valueOf();
    }

    private isPremarket(currentTime: Date): boolean {
        const currentTimeMarketTimeZone = DateHelpers.toMarketTimeZone(currentTime);
        const premarketStart = DateHelpers.getPreMarketOpenTime(currentTimeMarketTimeZone.toJSDate());
        const marketOpen = DateHelpers.getMarketOpenTime(currentTimeMarketTimeZone.toJSDate());
        return DateHelpers.isNormalWorkingDay(currentTimeMarketTimeZone) && currentTimeMarketTimeZone.valueOf() >= premarketStart.valueOf() && currentTimeMarketTimeZone.valueOf() <= marketOpen.valueOf();
    }

    private isPostmarket(currentTime: Date): boolean {
        const currentTimeMarketTimeZone = DateHelpers.toMarketTimeZone(currentTime);
        const postMarketStartTime = DateHelpers.getMarketCloseTime(currentTimeMarketTimeZone.toJSDate());
        const postMarketCloseTime = DateHelpers.getPostMarketCloseTime(currentTimeMarketTimeZone.toJSDate());
        return DateHelpers.isNormalWorkingDay(currentTimeMarketTimeZone) && currentTimeMarketTimeZone.valueOf() >= postMarketStartTime.valueOf() && currentTimeMarketTimeZone.valueOf() <= postMarketCloseTime.valueOf();
    }

    public setChangeFromClose_FromTopList(changeFromCloseDollar: number, changeFromClosePercentage: number) {
        this.changesFromCloseDollar = changeFromCloseDollar;
        this.changeFromClosePercentage = changeFromClosePercentage;
    }

    public setChangeFromClose_FromLastMarketOpen(changeFromCloseDollar: number, changeFromClosePercentage: number) {
        this.changesFromCloseDollar = changeFromCloseDollar;
        this.changeFromClosePercentage = changeFromClosePercentage;
    }

    public getLastMarketClose_ChangeFromClose(date: Date, lastPrice?: number): LastMarketCloseChangeFromCloseResult | null {
        const lastPointInMarketTimeZone = DateHelpers.toMarketTimeZone(date);
        const isMarketOpen = DateHelpers.isMarketOpen(date);
        const points = this.points;
        const marketSchedule = getHolidayManagerInstance().getMarketSchedule();
        let yesterdayMarketTimeZoneWithoutTime = lastPointInMarketTimeZone.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        if (isMarketOpen) {
            yesterdayMarketTimeZoneWithoutTime = yesterdayMarketTimeZoneWithoutTime.plus({ days: -1 });
        } else {
            let targetDate = yesterdayMarketTimeZoneWithoutTime.plus({ minute: marketSchedule.minutesUntilOpen });
            let retry = 0;
            while (!this.isMarketOpen(targetDate.toJSDate())) {
                targetDate = targetDate.plus({ days: -1 });

                if (this.isMarketOpen(targetDate.toJSDate())) {
                    break;
                }

                retry++;

                if (retry >= 100) {
                    break;
                }
            }

            yesterdayMarketTimeZoneWithoutTime = targetDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        }

        let pointsWithoutPreAndPostMarkets = points.filter((x: TIChartPointModel) => {
            const startTime = DateHelpers.toMarketTimeZone(x.start);
            // if (isMarketOpen) {
            return this.isMarketOpen(startTime.toJSDate()) && startTime < yesterdayMarketTimeZoneWithoutTime;
            // } else {
            //     return this.isMarketOpen(startTime.toJSDate()) && startTime < lastPointInMarketTimeZone;
            // }
        });

        pointsWithoutPreAndPostMarkets = pointsWithoutPreAndPostMarkets.sort((a, b) => a.startTimeT - b.startTimeT);
        const lastMarketClosePoint = pointsWithoutPreAndPostMarkets.length ? pointsWithoutPreAndPostMarkets[pointsWithoutPreAndPostMarkets.length - 1] : null;
        if (!lastMarketClosePoint) return null;

        const lastPriceToUse = lastPrice == undefined ? this.base.tiChartSeries.lastPrice : lastPrice;
        const changesFromCloseDollar = lastPriceToUse - lastMarketClosePoint.close;
        const changesFromClosePercentage = (changesFromCloseDollar / lastMarketClosePoint.close) * 100;

        return {
            symbol: this.base.tiChartSeries.symbol,
            requestTime: date,
            lastMarketCloseTime: lastMarketClosePoint.end,
            lastMarketCloseDataPoint: lastMarketClosePoint,
            lastMarketClosePrice: lastMarketClosePoint.close,
            lastPrice: lastPriceToUse,
            changesFromCloseDollar: changesFromCloseDollar,
            changesFromClosePercentage: changesFromClosePercentage
        };
    }

    private resolveGetLastPriceInLastMarketOpenDay: any;
    private getLastPriceInLastMarketOpenDayTimeInMarketTimeZone: any;
    public async getLastPriceInLastMarketOpenDay(date: Date) {
        const timeInMarketTimeZone = DateHelpers.toMarketTimeZone(date);

        this.getLastPriceInLastMarketOpenDayTimeInMarketTimeZone = timeInMarketTimeZone;
        this.resolveGetLastPriceInLastMarketOpenDay = null;
        return new Promise((r) => {
            this.resolveGetLastPriceInLastMarketOpenDay = r;
            if (!this.initLastPriceInLastMarketPoints) {
                this.base.chartService.requestSimpleTiDataPoints(this.symbol, this.lastPriceInLastMarketPointsTimeframe, false, true, 1000);
            } else {
                this.processAndReturnGetLastPriceInLastMarketOpenDay();
            }
        });
    }

    private processAndReturnGetLastPriceInLastMarketOpenDay() {
        let pointsWithoutPreAndPostMarkets = this.lastPriceInLastMarketPoints.filter((x: TIChartPointModel) => {
            const time = DateHelpers.toMarketTimeZone(x.end);
            return time < this.getLastPriceInLastMarketOpenDayTimeInMarketTimeZone;
        });

        pointsWithoutPreAndPostMarkets = pointsWithoutPreAndPostMarkets.sort((a, b) => a.startTimeT - b.startTimeT);
        const lastMarketClosePoint = pointsWithoutPreAndPostMarkets.length ? pointsWithoutPreAndPostMarkets[pointsWithoutPreAndPostMarkets.length - 1] : null;
        if (!lastMarketClosePoint) return null;

        this.resolveGetLastPriceInLastMarketOpenDay?.({
            symbol: this.base.tiChartSeries.symbol,
            requestTime: this.getLastPriceInLastMarketOpenDayTimeInMarketTimeZone,
            lastCloseTime: lastMarketClosePoint.end,
            lastCloseDataPoint: lastMarketClosePoint,
            lastClosePrice: lastMarketClosePoint.close
        });
    }
}